import type {
  AircallClickToCallApiData,
  AircallClickToCallApiResponses,
  NoticeAdminActiveListApiData,
  NoticeAdminActiveListApiResponses,
  NoticeAutoTranslateApiData,
  NoticeAutoTranslateApiResponses,
  NoticeCreateApiData,
  NoticeCreateApiResponses,
  NoticeDeleteApiData,
  NoticeDeleteApiResponses,
  NoticeGetApiData,
  NoticeGetApiResponses,
  NoticeListApiData,
  NoticeListApiResponses,
  NoticeLoginListApiData,
  NoticeLoginListApiResponses,
  NoticePartnerActiveListApiData,
  NoticePartnerActiveListApiResponses,
  NoticePatchApiData,
  NoticePatchApiResponses,
  NoticePublicListApiData,
  NoticePublicListApiResponses,
  NoticeTemplateCreateApiData,
  NoticeTemplateCreateApiResponses,
  NoticeTemplateDeleteApiData,
  NoticeTemplateDeleteApiResponses,
  NoticeTemplateEnableApiData,
  NoticeTemplateEnableApiResponses,
  NoticeTemplateGetApiData,
  NoticeTemplateGetApiResponses,
  NoticeTemplateListApiData,
  NoticeTemplateListApiResponses,
  TwilioSafeListBlockedCustomersApiData,
  TwilioSafeListBlockedCustomersApiResponses,
  TwilioSafeListBulkAddApiData,
  TwilioSafeListBulkAddApiResponses,
  TwilioSafeListEntryCreateApiData,
  TwilioSafeListEntryCreateApiResponses,
  TwilioSafeListEntryDeleteApiData,
  TwilioSafeListEntryDeleteApiResponses,
  TwilioSafeListEntryGetApiData,
  TwilioSafeListEntryGetApiResponses,
  TwilioSafeListEntryListApiData,
  TwilioSafeListEntryListApiResponses,
  TwilioSafeListEntryReportApiData,
  TwilioSafeListEntryReportApiResponses,
  TwilioSafeListReconcileApiData,
  TwilioSafeListReconcileApiResponses
} from '@navio/nsdk';

import { apiUrl, dataEndpoint, responseEndpoint } from '../defineResource';

export const getCommunicationUrls = {
  postAircallClickToCall: responseEndpoint<AircallClickToCallApiData, AircallClickToCallApiResponses>({
    getUrl: () => apiUrl('communication/aircall/calls/click-to-call/'),
    getBody: (data) => data
  }),
  getPublicNotices: dataEndpoint<NoticePublicListApiData, NoticePublicListApiResponses>({
    getUrl: (queryParams) => apiUrl('communication/notices/public/', queryParams)
  }),
  getPartnerActiveNotices: dataEndpoint<NoticePartnerActiveListApiData, NoticePartnerActiveListApiResponses>({
    getUrl: (queryParams) => apiUrl('communication/notices/partner-active/', queryParams)
  }),
  getAdminActiveNotices: dataEndpoint<NoticeAdminActiveListApiData, NoticeAdminActiveListApiResponses>({
    getUrl: () => apiUrl('communication/notices/admin-active/')
  }),
  getLoginNotices: dataEndpoint<NoticeLoginListApiData, NoticeLoginListApiResponses>({
    getUrl: (queryParams) => apiUrl('communication/notices/login/', queryParams)
  }),
  getPaginatedNotices: dataEndpoint<NoticeListApiData, NoticeListApiResponses>({
    getUrl: (queryParams) => apiUrl('communication/notices/', queryParams)
  }),
  getNotice: dataEndpoint<NoticeGetApiData, NoticeGetApiResponses>({
    getUrl: ({ noticeId }) => apiUrl(`communication/notices/${noticeId}/`)
  }),
  postNotice: responseEndpoint<NoticeCreateApiData, NoticeCreateApiResponses>({
    getUrl: () => apiUrl('communication/notices/'),
    getBody: (data) => data
  }),
  postNoticeAutoTranslate: responseEndpoint<NoticeAutoTranslateApiData, NoticeAutoTranslateApiResponses>({
    getUrl: () => apiUrl('communication/notices/auto-translate/'),
    getBody: (data) => data
  }),
  patchNotice: responseEndpoint<NoticePatchApiData, NoticePatchApiResponses>({
    getUrl: ({ noticeId }) => apiUrl(`communication/notices/${noticeId}/`),
    getBody: (data) => data
  }),
  deleteNotice: dataEndpoint<NoticeDeleteApiData, NoticeDeleteApiResponses>({
    getUrl: ({ noticeId }) => apiUrl(`communication/notices/${noticeId}/`)
  }),
  getPaginatedNoticeTemplates: dataEndpoint<NoticeTemplateListApiData, NoticeTemplateListApiResponses>({
    getUrl: (queryParams) => apiUrl('communication/notice-templates/', queryParams)
  }),
  getNoticeTemplate: dataEndpoint<NoticeTemplateGetApiData, NoticeTemplateGetApiResponses>({
    getUrl: ({ noticeTemplateId }) => apiUrl(`communication/notice-templates/${noticeTemplateId}/`)
  }),
  postNoticeTemplate: responseEndpoint<NoticeTemplateCreateApiData, NoticeTemplateCreateApiResponses>({
    getUrl: () => apiUrl('communication/notice-templates/'),
    getBody: (data) => data
  }),
  deleteNoticeTemplate: dataEndpoint<NoticeTemplateDeleteApiData, NoticeTemplateDeleteApiResponses>({
    getUrl: ({ noticeTemplateId }) => apiUrl(`communication/notice-templates/${noticeTemplateId}/`)
  }),
  postNoticeTemplateEnable: responseEndpoint<NoticeTemplateEnableApiData, NoticeTemplateEnableApiResponses>({
    getUrl: ({ noticeTemplateId }) => apiUrl(`communication/notice-templates/${noticeTemplateId}/enable/`),
    getBody: (data) => data
  }),
  getPaginatedTwilioSafeListEntries: dataEndpoint<TwilioSafeListEntryListApiData, TwilioSafeListEntryListApiResponses>({
    getUrl: (queryParams) => apiUrl('communication/twilio-safe-list/', queryParams)
  }),
  getTwilioSafeListEntry: dataEndpoint<TwilioSafeListEntryGetApiData, TwilioSafeListEntryGetApiResponses>({
    getUrl: ({ twilioSafeListEntryId }) => apiUrl(`communication/twilio-safe-list/${twilioSafeListEntryId}/`)
  }),
  postTwilioSafeListEntry: responseEndpoint<TwilioSafeListEntryCreateApiData, TwilioSafeListEntryCreateApiResponses>({
    getUrl: () => apiUrl('communication/twilio-safe-list/'),
    getBody: (data) => data
  }),
  deleteTwilioSafeListEntry: dataEndpoint<TwilioSafeListEntryDeleteApiData, TwilioSafeListEntryDeleteApiResponses>({
    getUrl: ({ twilioSafeListEntryId }) => apiUrl(`communication/twilio-safe-list/${twilioSafeListEntryId}/`)
  }),
  reportTwilioSafeListEntry: responseEndpoint<TwilioSafeListEntryReportApiData, TwilioSafeListEntryReportApiResponses>({
    getUrl: ({ twilioSafeListEntryId }) => apiUrl(`communication/twilio-safe-list/${twilioSafeListEntryId}/report/`)
  }),
  reconcileTwilioSafeList: responseEndpoint<TwilioSafeListReconcileApiData, TwilioSafeListReconcileApiResponses>({
    getUrl: () => apiUrl('communication/twilio-safe-list/reconcile/')
  }),
  getTwilioSafeListBlockedCustomers: dataEndpoint<
    TwilioSafeListBlockedCustomersApiData,
    TwilioSafeListBlockedCustomersApiResponses
  >({
    getUrl: () => apiUrl('communication/twilio-safe-list/blocked-customers/')
  }),
  postTwilioSafeListBulkAdd: responseEndpoint<TwilioSafeListBulkAddApiData, TwilioSafeListBulkAddApiResponses>({
    getUrl: () => apiUrl('communication/twilio-safe-list/blocked-customers/'),
    getBody: (data) => data
  })
};
